import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";

// ─── Consommations setup (client 2026-09-01, approved) ────────────────────────
//
// Replaces the name-based guessing the report used to do. For each Consommations
// line the user picks:
//   • the METER(S) giving the building total — several are summed, because a total is
//     not always one meter (Confédération Centre's Eau is three "Introduction
//     compteur SI" mains);
//   • where the MONEY comes from — cost accounts and/or the result of a KEY. The key
//     option exists because money can arrive through a key rather than a named
//     account ("result from key Chaufferie Générale 2 is Eau chaude").
//
// Settings are per BUILDING and carry into new periods, so a project is set up once.
// The meter/cost/key catalogues are per period, so the editor shows the selected
// period's lists — but stores the CODES, which is what makes the mapping survive a
// re-import (see tbl_energy_mapping).
//
// It saves through its own endpoint rather than the building form's submit: this is a
// separate, occasional task, and threading six lines of multi-selects through the
// building Formik would couple two unrelated saves.

const authHeaders = () => ({
  "api-key": process.env.REACT_APP_API_KEY,
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// A line's identity is (label, unit) — "Eau chaude" appears twice, kWh and m3.
const lineKey = (l) => `${String(l.label).trim()}|${String(l.unit).trim()}`;

const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("de-CH", { maximumFractionDigits: 2 });
};

// Column widths. Key(s) is deliberately as wide as the other pickers: a key name like
// "Chaufferie Générale 2" is ~21 characters, and two of them selected together need the
// room. Sum is TABLE_MIN_WIDTH, below which .table-responsive scrolls.
const COL = { show: 64, line: 145, meters: 270, costs: 270, keys: 285 };
const TABLE_MIN_WIDTH = Object.values(COL).reduce((a, b) => a + b, 0);

// Chip text budget. Long enough to identify a meter or account, short enough that
// three selections still sit on one or two lines inside the column.
const SHORT_MAX = 22;
const shorten = (t) => {
  const s = String(t || "").trim();
  return s.length <= SHORT_MAX ? s : `${s.slice(0, SHORT_MAX - 1)}…`;
};

// Long label in the menu, short label in the selected chip. `context` is "menu" while
// the dropdown is open and "value" for a chip, which is exactly the distinction needed.
const formatOptionLabel = (opt, { context }) =>
  context === "value" ? (opt.short || opt.label) : opt.label;

// react-select styling kept minimal and inherited so the control matches the
// surrounding Bootstrap form rather than importing a second visual language.
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 34, fontSize: 13 }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 5 }),
  multiValue: (base) => ({ ...base, fontSize: 12 }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  // The menus are portalled to <body> so they are not clipped by the table's
  // overflow — which also takes them out of the form's stacking context, so they need
  // an explicit z-index or they render BEHIND the surrounding chrome.
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Used for the cost picker while a key is selected on the same line: the selections are
// kept (so nothing the user typed is lost) but visibly stood down, matching the warning.
const selectStylesMuted = {
  ...selectStyles,
  control: (base) => ({ ...base, minHeight: 34, fontSize: 13, opacity: 0.5 }),
};

const EnergyMappingEditor = ({ buildingId, periodId, periodLabel }) => {
  const [lines, setLines] = useState([]);
  const [options, setOptions] = useState({ meters: [], costs: [], keys: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { kind: 'ok'|'err', text }

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}energy-mapping`, {
        params: { building_id: buildingId, ...(periodId ? { period_id: periodId } : {}) },
        headers: authHeaders(),
      });
      const data = res?.data?.data || {};
      setLines(data.lines || []);
      setOptions(data.options || { meters: [], costs: [], keys: [] });
    } catch (err) {
      setStatus({ kind: "err", text: err?.response?.data?.message || err?.message || "Could not load the configuration." });
    } finally {
      setLoading(false);
    }
  }, [buildingId, periodId]);

  useEffect(() => { load(); }, [load]);

  // Every option carries TWO labels. `label` is the long, descriptive one shown in the
  // dropdown — the catalogue runs to 228 meters on Confédération Centre and you cannot
  // tell "Compteur SIG" from "Compteur général" without the value and unit. `short` is
  // what the SELECTED chip shows.
  //
  // They have to differ. Rendering the long label inside the chip (verified in a
  // headless-Chrome screenshot) wrapped every selection over three or four lines and
  // made the Eau row ~250 px tall on its own, because a label like
  // "200 — Introduction compteur SI - Confédération 6 (4'956 m3) · Eau" is wider than
  // the whole column. The chip therefore shows the code plus a clipped name, which is
  // enough to recognise a selection, and the full text stays one hover away in the menu.
  const meterOptions = useMemo(
    () => (options.meters || []).map((m) => ({
      value: m.code,
      label: `${m.code} — ${m.name}${m.value ? ` (${fmtNum(m.value)} ${m.unit || ""})` : ""}${m.energie ? ` · ${m.energie}` : ""}`,
      short: `${m.code}${m.name ? ` ${shorten(m.name)}` : ""}`,
    })),
    [options.meters]
  );
  const costOptions = useMemo(
    () => (options.costs || []).map((c) => ({
      value: c.number,
      label: `${c.number} — ${c.name}${c.value ? ` (${fmtNum(c.value)})` : ""}`,
      short: `${c.number}${c.name ? ` ${shorten(c.name)}` : ""}`,
    })),
    [options.costs]
  );
  const keyOptions = useMemo(
    () => (options.keys || []).map((k) => ({ value: k, label: k })),
    [options.keys]
  );

  // Selected values may reference a code that is absent from THIS period's catalogue
  // (a meter renamed, or a period that never had it). Those must still render, and
  // must survive a save untouched — silently dropping them would quietly break the
  // mapping for the periods where the code does exist.
  const pick = (opts, codes) =>
    (codes || []).map((c) => opts.find((o) => o.value === String(c)) || {
      value: String(c),
      label: `${c} (not in ${periodLabel || "this period"})`,
      short: `${c} (not in period)`,
    });

  const patchLine = (k, patch) =>
    setLines((prev) => prev.map((l) => (lineKey(l) === k ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}energy-mapping`,
        { building_id: buildingId, lines },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      setLines(res?.data?.data?.lines || lines);
      setStatus({ kind: "ok", text: "Consommations setup saved." });
    } catch (err) {
      setStatus({ kind: "err", text: err?.response?.data?.message || err?.message || "Could not save the configuration." });
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = lines.filter((l) => l.enabled).length;

  return (
    <div className="col-12">
      <div className="form-group">
        <label className="mb-1">Consommations</label>
        <small className="d-block text-muted mb-2">
          For each line, choose the meter(s) that give the building total and where the
          amount comes from — <strong>either</strong> cost accounts <strong>or</strong> a
          key, not both: a key already includes every cost that reaches it, so counting
          both would charge the same amount twice. Applies to all periods of this
          building. Lines with nothing selected show “–” on the report.
          {periodLabel ? ` Meter and cost lists are shown for ${periodLabel}.` : ""}
        </small>

        {loading && <div className="text-muted small">Loading…</div>}

        {!loading && lines.length > 0 && (
          <div className="table-responsive">
            {/* Client 2026-09-09: "Can you make the Key collumn vision larger on the
                Edit Building page?" The three picker columns carried no width, so the
                browser sized them to their CONTENT — the cost column with four long
                chips took the space and squeezed Key down to about 120 px, where even
                the placeholder wrapped over three lines and a selected key rendered as
                a couple of characters.
                Fixed widths make the columns independent of what happens to be selected,
                and `minWidth` with the surrounding .table-responsive means a narrow
                window scrolls the table rather than crushing a column again. */}
            <table
              className="table table-sm align-middle"
              style={{ fontSize: 13, tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH }}
            >
              <colgroup>
                <col style={{ width: COL.show }} />
                <col style={{ width: COL.line }} />
                <col style={{ width: COL.meters }} />
                <col style={{ width: COL.costs }} />
                <col style={{ width: COL.keys }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Show</th>
                  <th>Line</th>
                  <th>Meter(s) — building total</th>
                  <th>Cost account(s)</th>
                  <th>Key(s)</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const k = lineKey(l);
                  return (
                    <tr key={k} style={l.enabled ? undefined : { opacity: 0.55 }}>
                      <td>
                        <div
                          className={`toggle-switch${l.enabled ? " active" : ""}`}
                          onClick={() => patchLine(k, { enabled: !l.enabled })}
                          role="switch"
                          aria-checked={l.enabled}
                          aria-label={`${l.label} ${l.unit}`}
                        >
                          <span className="toggle-knob" />
                        </div>
                      </td>
                      <td>
                        <strong>{l.label}</strong>
                        <span className="text-muted"> {l.unit}</span>
                      </td>
                      <td>
                        <Select
                          isMulti
                          isDisabled={!l.enabled}
                          options={meterOptions}
                          value={pick(meterOptions, l.meter_codes)}
                          onChange={(v) => patchLine(k, { meter_codes: (v || []).map((o) => o.value) })}
                          placeholder="Select meter(s)…"
                          styles={selectStyles}
                          closeMenuOnSelect={false}
                          formatOptionLabel={formatOptionLabel}
                          menuPortalTarget={document.body}
                        />
                      </td>
                      <td>
                        {/* A key wins over cost accounts on the same line (see the
                            keys-win note in getObjectDetail). Rather than silently
                            dropping what the user picked, say so where they picked it —
                            client 2026-09-04 selected key "Froid 1" AND the four
                            accounts that feed it, which reported CHF/unité 0.371
                            instead of 0.138. */}
                        <Select
                          isMulti
                          isDisabled={!l.enabled}
                          options={costOptions}
                          value={pick(costOptions, l.cost_numbers)}
                          onChange={(v) => patchLine(k, { cost_numbers: (v || []).map((o) => o.value) })}
                          placeholder="Select cost account(s)…"
                          styles={(l.key_names || []).length ? selectStylesMuted : selectStyles}
                          closeMenuOnSelect={false}
                          formatOptionLabel={formatOptionLabel}
                          menuPortalTarget={document.body}
                        />
                        {(l.key_names || []).length > 0 && (l.cost_numbers || []).length > 0 && (
                          // Bootstrap's .text-warning is #ffc107, designed for dark
                          // grounds — on white it was barely legible in a rendered
                          // screenshot. An explicit dark amber keeps the warning
                          // semantics with contrast that actually reads.
                          <div className="small mt-1" style={{ fontSize: 11, lineHeight: 1.3, color: "#8A5A00", fontWeight: 500 }}>
                            Ignored — a key is selected, and it already includes the costs
                            that reach it. Remove the key to use these accounts instead.
                          </div>
                        )}
                      </td>
                      <td>
                        <Select
                          isMulti
                          isDisabled={!l.enabled}
                          options={keyOptions}
                          value={pick(keyOptions, l.key_names)}
                          onChange={(v) => patchLine(k, { key_names: (v || []).map((o) => o.value) })}
                          placeholder="Optional — select key(s)…"
                          styles={selectStyles}
                          closeMenuOnSelect={false}
                          formatOptionLabel={formatOptionLabel}
                          menuPortalTarget={document.body}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div className="d-flex align-items-center gap-3 mt-2">
            <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Consommations setup"}
            </button>
            <span className="text-muted small">{enabledCount} of {lines.length} lines shown on the report</span>
            {status && (
              <span className={`small ${status.kind === "ok" ? "text-success" : "text-danger"}`}>
                {status.text}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnergyMappingEditor;
